/**
 * Pricing logic for listPrice salePrice and kitPrice
 *
 * @module views/components/ProductPricing
 * @memberof -Common
 */

import './ProductPricing.scss';

import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Text as PalText } from 'web-palette/dist/components/Text/Text';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Price from '@ulta/core/components/Price/Price';
import Text from '@ulta/core/components/Text/Text';
import { OVERLAY_TYPE, useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Tag from '@ulta/modules/Tag/Tag';

import AfterPay from '@ulta/components/Afterpay/AfterPay';
import FormattedTextLink, { FORMATTED_LINK_TYPE, parse } from '@ulta/components/FormattedTextLink/FormattedTextLink';
import ProductOffersCard from '@ulta/components/ProductOffersCard/ProductOffersCard';

import IframedContent from '../IframedContent/IframedContent';

/**
  * Represents a ProductPricing component
  *
  * @method
  * @param {ProductPricingProps} props - React properties passed from composition
  * @returns {JSX.Element} - Price
  */
export const ProductPricing = function( props ){
  const {
    listPrice,
    originalPriceLabel,
    listPriceAccessibilityLabel,
    salePrice,
    salePriceAccessibilityLabel,
    kitPrice,
    id,
    message,
    appDownloadLabel,
    appDownloadAction,
    offers,
    subscriptionEligible,
    promotionTags,
    priceLabel,
    componentKey,
    disabled,
    noPrice,
    minThreshold,
    isPaymentInfoEnabled,
    paymentInfo
  } = props;

  // Set to false initially so both components appear together once Afterpay is VISUALLY ready
  const [isAfterpaySdkReady, setIsAfterpaySdkReady] = useState( false );

  const afterpay = paymentInfo?.afterpay;
  const klarnaInfo = paymentInfo?.klarnaInfo || null;
  const staticPaymentInfo = paymentInfo?.staticPaymentInfo;
  const isStaticInfoEnabled = !!paymentInfo?.isStaticInfoEnabled;
  const shouldDisplayKlarnaAndAfterPay = !isStaticInfoEnabled && ( !!klarnaInfo && !!afterpay );
  const paymentGroupClassName = classNames( 'ProductPricing__PaymentSubsGroup', {
    'ProductPricing__PaymentSubsGroup--pending': shouldDisplayKlarnaAndAfterPay && !isAfterpaySdkReady
  } );
  const displaySubscription = !!subscriptionEligible;
  const displayOffers = offers?.length > 0;
  const hasItemsPromotionTags = hasItems( promotionTags );
  const hasSinglePromotion = hasItemsPromotionTags && promotionTags.length === 1;
  const hasMultiplePromotions = hasItemsPromotionTags && promotionTags.length > 1;
  const { openOverlay, closeOverlay } = useOverlay();
  const klarnaButtonref = useRef( null );

  // Fix for Gift SKUs (Beauty Break GWPs): Handle kitPrice-only case directly
  // The web-palette Price component doesn't render kitPrice when there's no listPrice/salePrice
  const isKitPriceOnly = kitPrice && !listPrice && !salePrice;

  return (
    <div className='ProductPricing'
      id={ id }
      aria-live={ 'polite' }
      aria-atomic={ true }
    >
      { afterpay?.afterpaySdkSrc && (
        <link id='AfterPay--preload' rel='preload' href={ afterpay.afterpaySdkSrc } as='script' />
      ) }
      <>
        { hasSinglePromotion && (
          <div className='ProductPricing__PromotionSingleTag'>
            <Tag
              { ...spreadProps( { ...promotionTags[0] } ) }
            />
          </div>
        ) }
        { hasMultiplePromotions && (
          <ul className='ProductPricing__PromotionMultipleTag'>
            { promotionTags.map( ( item, index ) => (
              <li key={ `${componentKey}-tag-${index}` }>
                <Tag
                  { ...spreadProps( { ...item } ) }
                />
              </li>
            ) ) }
          </ul>
        ) }
        { isKitPriceOnly && (
          <div className='ProductPricing__kitPriceOnly'>
            <span className='sr-only'>{ `Kit Price ${ kitPrice }` }</span>
            <Text
              htmlTag='span'
              textStyle='body-3'
              color='neutral-600'
              ariaHidden={ true }
            >
              { kitPrice }
            </Text>
            { message && (
              <Text
                htmlTag='p'
                textStyle='body-3'
                color='neutral-600'
              >
                { message }
              </Text>
            ) }
          </div>
        ) }
        { !isKitPriceOnly && (
          <Price
            priceLabel={ priceLabel }
            listPrice={ listPrice }
            originalPriceLabel={ originalPriceLabel }
            listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
            salePrice={ salePrice }
            salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
            kitPrice={ kitPrice }
            variant='PDP'
            message={ message }
            appDownloadLabel={ appDownloadLabel }
            appDownloadAction={ appDownloadAction }
            componentKey={ componentKey }
            disabled={ disabled }
            noPrice={ noPrice }
          />
        ) }
      </>
      <div className={ paymentGroupClassName }>
        { !!isPaymentInfoEnabled && (
          shouldDisplayKlarnaAndAfterPay ? (
            <>
              <AfterPay
                product={ afterpay }
                minThreshold={ minThreshold }
                onSdkReady={ () => setIsAfterpaySdkReady( true ) }
              />
              <Klarna
                { ...spreadProps( {
                  ...klarnaInfo,
                  klarnaLogo: staticPaymentInfo?.klarnaLogo,
                  klarnaInfoAriaLabel: klarnaInfo?.klarnaInfoAriaLabel
                } ) }
                openOverlay={ openOverlay }
                klarnaButtonref={ klarnaButtonref }
              />
            </>
          ) : (
            <StaticPaymentInfo
              { ...spreadProps( {
                ...staticPaymentInfo,
                klarnaInfoAriaLabel: klarnaInfo?.klarnaInfoAriaLabel
              } ) }
            />
          )
        ) }
      </div>
      { displaySubscription &&
      <div className='ProductPricing__Subscription'>
        <Tag
          variant='utility'
          message={ props.subscriptionEligible.subsLabel }
          icon={ props.subscriptionEligible.subsIcon }
        />
      </div>
      }
      { displayOffers && <ProductOffersCard { ...spreadProps( { ...props } ) } /> }
    </div>

  );
};


/**
 * @typedef {object} KlarnaProps
 * @property {string} klarnaLogo - The name of the Klarna logo icon.
 * @property {object} selectAction - The action to perform when the info button is clicked.
 * @property {string} selectAction.url - The URL to open in a popup.
 * @property {string} infoIcon - The name of the info icon.
 * @property {string} prefix - Text to display before the logo.
 */

/**
 * A component that displays Klarna payment information.
 *
 * @param {KlarnaProps} props - The props for the component.
 * @returns {JSX.Element|null} The rendered component, or null if there is no logo.
 */
const Klarna = ( props ) => {
  const {
    selectAction,
    klarnaLogo,
    infoIcon,
    prefix,
    openOverlay,
    klarnaButtonref,
    klarnaInfoAriaLabel
  } = props;


  const handleClick = () => {
    if( !selectAction?.url ){
      return;
    }

    const KlarnaContent =
      <IframedContent fullScreenPageDisplay={ false }
        contentUrl={ selectAction?.url }
        modalDisplay={ true }
      />;

    openOverlay( {
      type: OVERLAY_TYPE.Modal,
      crossButtonVisibility: true,
      opener: klarnaButtonref,
      content: KlarnaContent,
      customClassName:'KlarnaModal',
      heightPercentageOverride:100
    } );
  };

  if( !klarnaLogo ){
    return null;
  }

  return (
    <div className='ProductPricing__Klarna'>
      <PalText
        textColor='default'
        textStyle='body-sm'
        textBold={ false }
      >
        { prefix }
      </PalText>
      <div className='ProductPricing__Klarna--Logo'>
        <Icon name={ klarnaLogo } size='custom' />
      </div>
      <Button
        className='ProductPricing__Klarna--Info'
        onClick={ handleClick }
        ref={ klarnaButtonref }
        ariaLabel={ klarnaInfoAriaLabel }
        variant='unstyled'
      >
        <Icon name={ infoIcon } />
      </Button>
    </div>
  );
};

Klarna.propTypes = {
  /** The name of the Klarna logo icon. */
  klarnaLogo: PropTypes.string,
  /** The action to perform when the info button is clicked. */
  selectAction: PropTypes.shape( {
    /** The URL to open in a popup. */
    url: PropTypes.string
  } ),
  /** The name of the info icon. */
  infoIcon: PropTypes.string,
  /** Text to display before the logo. */
  prefix: PropTypes.string,
  /** Accessibility label for the Klarna info */
  klarnaInfoAriaLabel: PropTypes.string
};

/**
 * @typedef {object} StaticPaymentInfoProps
 * @property {string} [text] - The text to display for the `{text}` placeholder.
 * @property {(string|object)} [afterPayLogo] - The name of the AfterPay logo icon for the `{afterPayLogo}` placeholder.
 * @property {(string|object)} [klarnaLogo] - The name of the Klarna logo icon for the `{klarnaLogo}` placeholder.
 * @property {string} dynamicPlacement - A string that dictates the order and content, with placeholders like `{text}`, `{afterPayLogo}`, `{klarnaLogo}`.
 */

/**
 * Renders static payment information, dynamically arranging text and logos.
 * The layout is determined by the `dynamicPlacement` prop, which is a string
 * containing placeholders that get replaced with the corresponding components.
 *
 * @param {StaticPaymentInfoProps} props - The props for the component.
 * @returns {JSX.Element|null} The rendered static payment info, or null if `dynamicPlacement` is missing.
 */
const StaticPaymentInfo = ( props ) => {
  const {
    dynamicPlacement,
    text,
    afterPayLogo = 'afterPayLogoRegular',
    klarnaLogo = 'klarnaLogo',
    klarnaInfoAriaLabel
  } = props;

  if( !dynamicPlacement ){
    return null;
  }

  const parsedParts = parse( { str: dynamicPlacement } );

  const tokenMap = {
    text: { type: FORMATTED_LINK_TYPE.Text, value: text },
    afterPayLogo: {
      type: FORMATTED_LINK_TYPE.Icon,
      value: {
        name: afterPayLogo,
        size: 'custom',
        wrapperClassName: 'ProductPricing__StaticPaymentInfo--AfterpayLogo'
      }
    },
    klarnaLogo: {
      type: FORMATTED_LINK_TYPE.Icon,
      value: {
        name: klarnaLogo,
        size: 'custom',
        ariaLabel: klarnaInfoAriaLabel,
        wrapperClassName: 'ProductPricing__Klarna--Logo'
      }
    }
  };
  return (
    <FormattedTextLink
      className='ProductPricing__StaticPaymentInfo'
      parsedParts={ parsedParts }
      tokenMap={ tokenMap }
    />
  );
};

StaticPaymentInfo.propTypes = {
  /** A string that dictates the order and content, with placeholders like `{text}`, `{afterPayLogo}`, `{klarnaLogo}`. */
  dynamicPlacement: PropTypes.string,
  /** The text to display for the `{text}` placeholder. */
  text: PropTypes.string,
  /** The name of the AfterPay logo icon for the `{afterPayLogo}` placeholder. */
  afterPayLogo: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
  /** The name of the Klarna logo icon for the `{klarnaLogo}` placeholder. */
  klarnaLogo: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] )
};

/**
  * property type definitions
  * @typedef ProductPricingProps
  * @type {object}
  * @property {string} listPrice - Sets the list price to display
  * @property {string} originalPriceLabel - Sets the original price text to display with list price
  * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
  * @property {string} salePrice - Sets the sale price to display
  * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
  * @property {string} kitPrice - Sets the kit value to display`
  * @property {string} message - Sets the message to display
  * @property {string} priceLabel - Sets the price label to display
  * @property {string} appDownloadLabel - Sets the app download label to display
  * @property {object} appDownloadAction - Sets the app download action
  * @property {string} id - Sets the id of the main div
  * @property {string} componentKey - Sets the componentKey
  * @property {bool} disabled - Sets the disabled value
  * @property {object} noPrice - Sets the noPrice value
  * @property {(string|number)} minThreshold - Sets minimum purchase amount for AfterPay in sdk
  * @property {string} klarnaInfoAriaLabel - Accessibility label for the Klarna info
  * @property {bool} isPaymentInfoEnabled - Do not display payment info if this flag is off
 * @property {object} paymentInfo - Sets the payment info payload
 * @property {object} paymentInfo.afterpay - Sets the AfterPay info payload
 * @property {(bool|string)} paymentInfo.afterpay.eligible - Sets AfterPay eligibility
 * @property {string} paymentInfo.afterpay.currency - Sets the AfterPay currency code
 * @property {(string|number)} paymentInfo.afterpay.price - Sets the AfterPay price value
 * @property {object} paymentInfo.klarnaInfo - Sets the Klarna info payload
 * @property {(string|object)} paymentInfo.klarnaInfo.klarnaLogo - Sets the Klarna logo asset
 * @property {object} paymentInfo.klarnaInfo.selectAction - Sets the Klarna action
 * @property {string} paymentInfo.klarnaInfo.selectAction.url - Sets the Klarna info URL
 * @property {string} paymentInfo.klarnaInfo.infoIcon - Sets the Klarna info icon asset
 * @property {string} paymentInfo.klarnaInfo.prefix - Sets the Klarna prefix text
 * @property {string} paymentInfo.klarnaInfo.klarnaInfoAriaLabel - Sets the Klarna info aria label
 * @property {bool} paymentInfo.isStaticInfoEnabled - Determines whether static payment info should be displayed
 * @property {object} paymentInfo.staticPaymentInfo - Sets the static payment info payload
 * @property {string} paymentInfo.staticPaymentInfo.text - Sets the static payment info text
  * @property {(string|object)} paymentInfo.staticPaymentInfo.klarnaLogo - Sets the static Klarna logo asset
  * @property {(string|object)} paymentInfo.staticPaymentInfo.afterPayLogo - Sets the static AfterPay logo asset
  * @property {string} paymentInfo.staticPaymentInfo.dynamicPlacement - Sets the token order for static payment content
  */
export const propTypes = {

  /** Sets the list price to display */
  listPrice: PropTypes.string,
  /** Sets the original price text to display with list price */
  originalPriceLabel: PropTypes.string,
  /** Sets the list price accessibility label for screen readers */
  listPriceAccessibilityLabel: PropTypes.string,
  /** Sets the sale price to display */
  salePrice: PropTypes.string,
  /** Sets the sale price accessibility label for screen readers */
  salePriceAccessibilityLabel: PropTypes.string,
  /** Sets the kit value to display */
  kitPrice: PropTypes.string,
  /** Sets the message to display */
  message: PropTypes.string,
  /** Sets the price label to display */
  priceLabel: PropTypes.string,
  /** Sets the app download label to display */
  appDownloadLabel: PropTypes.string,
  /** Sets the app download action */
  appDownloadAction: PropTypes.object,
  /** Sets the id of the main div */
  id: PropTypes.string,
  /** Sets the componentKey */
  componentKey: PropTypes.string,
  /** Sets the disabled state of the price */
  disabled: PropTypes.bool,
  /** No Info on this Prop Yet */
  noPrice: PropTypes.object,
  /** Sets minimum purchase amount for AfterPay in sdk */
  minThreshold: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
  /** Accessibility label for the Klarna info */
  klarnaInfoAriaLabel: PropTypes.string,
  /** Determines whether payment info can be displayed */
  isPaymentInfoEnabled: PropTypes.bool,
  /** Sets the payment info payload */
  paymentInfo: PropTypes.shape( {
    /** Sets the AfterPay info payload */
    afterpay: PropTypes.shape( {
      /** Sets AfterPay eligibility */
      eligible: PropTypes.oneOfType( [PropTypes.bool, PropTypes.string] ),
      /** Sets the AfterPay currency code */
      currency: PropTypes.string,
      /** Sets the AfterPay price amount */
      price: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
      afterpaySdkSrc: PropTypes.string
    } ),
    /** Sets the Klarna info payload */
    klarnaInfo: PropTypes.shape( {
      /** Sets the Klarna logo asset */
      klarnaLogo: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
      /** Sets the Klarna action */
      selectAction: PropTypes.shape( {
        /** Sets the Klarna info URL */
        url: PropTypes.string
      } ),
      /** Sets the Klarna info icon asset */
      infoIcon: PropTypes.string,
      /** Sets the Klarna prefix text */
      prefix: PropTypes.string,
      /** Sets the Klarna info aria label */
      klarnaInfoAriaLabel: PropTypes.string
    } ),
    /** Determines whether static payment info should be displayed */
    isStaticInfoEnabled: PropTypes.bool,
    /** Sets the static payment info payload */
    staticPaymentInfo: PropTypes.shape( {
      /** Sets the static payment info text */
      text: PropTypes.string,
      /** Sets the static Klarna logo asset */
      klarnaLogo: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
      /** Sets the static AfterPay logo asset */
      afterPayLogo: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
      /** Sets the token order for static payment content */
      dynamicPlacement: PropTypes.string
    } )
  } )
};

export const defaultProps = {
  isPaymentInfoEnabled: false,
  paymentInfo: {
    afterpay: {},
    klarnaInfo: {},
    staticPaymentInfo: {},
    isStaticInfoEnabled: false
  },
  offers: [],
  promotionTags: []
};

ProductPricing.propTypes = propTypes;
ProductPricing.defaultProps = defaultProps;
ProductPricing.displayName = 'ProductPricing';

export default ProductPricing;